/**
 * TinyMCE Media Library Plugin
 *
 * Integrates WPS Pages media library with TinyMCE's image insertion.
 * Replaces the default TinyMCE image dialog with our custom media picker.
 */

import { uploadErrorMessage, renderUploadFailures, clearUploadFailures } from '../../wps_pages/upload_errors';

// Escape user-controlled values before interpolating into innerHTML (matches the
// pattern used in tools/WYSIWYG.js). Prevents stored XSS via media titles/filenames.
const escapeHtml = (text) =>
    String(text == null ? '' : text).replace(/[&<>"']/g, (m) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
    }[m]));

// Only allow http(s) or root-relative URLs as image sources (blocks javascript: URIs).
const isSafeUrl = (url) => typeof url === 'string' && /^(https?:|\/)/i.test(url);

export class MediaLibraryPlugin {
    constructor(editor) {
        this.editor = editor;
        this.modalElement = null;
        this.stimulusController = null;
    }

    /**
     * Initialize the plugin
     * Sets up the toolbar button and registers commands
     */
    init() {
        const editor = this.editor;

        console.log('📸 Initializing Media Library Plugin for editor:', editor.id);

        editor.ui.registry.addIcon('medialibraryicon', '<svg width="24" height="24" viewBox="0 0 24 24"><path d="M7 3h13a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Zm1 2v8.2l2.6-2.6a1 1 0 0 1 1.4 0l2.3 2.3 2.1-2.1a1 1 0 0 1 1.4 0L19 12V5H8Zm11 11v-1.2l-2.9-2.9-2.1 2.1a1 1 0 0 1-1.4 0l-2.3-2.3L8 15.9V16h11ZM4 6v13a1 1 0 0 0 1 1h13v2H5a3 3 0 0 1-3-3V6h2Zm12.5 1.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Z" fill-rule="evenodd"/></svg>');

        // Register the media library button with icon + text label
        editor.ui.registry.addButton('medialibrary', {
            icon: 'medialibraryicon',
            text: 'Media Library',
            tooltip: 'Media Library',
            onAction: () => {
                this.openMediaPicker();
            }
        });

        // Register as a menu item too
        editor.ui.registry.addMenuItem('medialibrary', {
            icon: 'image',
            text: 'Image from library',
            onAction: () => {
                this.openMediaPicker();
            }
        });

        // Also register a command for programmatic access
        editor.addCommand('mceMediaLibrary', () => {
            this.openMediaPicker();
        });

        console.log('✅ Media Library Plugin initialized');
        console.log('📍 Button registered as "medialibrary" in toolbar');
        console.log('📍 Current toolbar config:', editor.options.get('toolbar'));
    }

    /**
     * Open the media picker modal
     * Creates a temporary Stimulus controller to handle the selection
     */
    openMediaPicker() {
        console.log('🖼️  Opening media picker...');

        // Check if there's a selected image in TinyMCE
        const selectedImage = this.getSelectedImage();
        if (selectedImage) {
            console.log('📸 Found selected image:', selectedImage);
            this.selectedImageElement = selectedImage;
        } else {
            this.selectedImageElement = null;
        }

        // Create a temporary container for the media picker
        const container = document.createElement('div');
        // DON'T hide the container - the dialog inside needs to be visible
        container.setAttribute('data-controller', 'media-picker');
        container.setAttribute('data-media-picker-media-type-value', 'image');
        container.setAttribute('data-media-picker-multiple-value', 'false');
        container.setAttribute('data-media-picker-field-name-value', 'tinymce_image');

        // Add required targets
        container.innerHTML = `
            <div data-media-picker-target="input"></div>
            <div data-media-picker-target="preview"></div>
        `;

        // Find and render the media picker modal partial
        // We need to create the modal structure that matches _picker_modal.html.erb
        const modalElement = this.createMediaPickerModal(container);

        // Append to body
        document.body.appendChild(container);

        console.log('📦 Container appended to body');
        console.log('📦 Modal element:', modalElement);

        // Initialize Alpine/Stimulus on the container
        if (window.Alpine && window.Alpine.initTree) {
            window.Alpine.initTree(container);
            console.log('🎨 Alpine initialized on container');
        }

        // Wait for Stimulus to initialize, then open the modal
        setTimeout(() => {
            console.log('⏰ Opening modal directly...');

            // Directly open the modal using the native showModal() method
            if (modalElement && typeof modalElement.showModal === 'function') {
                // Add backdrop styles dynamically
                const style = document.createElement('style');
                style.textContent = `
                    dialog[data-media-picker-target="modal"]::backdrop {
                        background: rgba(0, 0, 0, 0.5);
                        backdrop-filter: blur(4px);
                    }
                    dialog[data-media-picker-target="modal"] {
                        z-index: 9999 !important;
                    }
                `;
                document.head.appendChild(style);

                modalElement.showModal();
                console.log('✅ Modal opened via showModal()');
                console.log('📊 Modal open state:', modalElement.open);
                console.log('📊 Modal display:', window.getComputedStyle(modalElement).display);
                console.log('📊 Modal z-index:', window.getComputedStyle(modalElement).zIndex);

                // Show loading state immediately
                this.showLoading();

                // Now find the controller to set up event handlers and load media
                const controllerElement = container;

                // Poll for controller to be ready
                this.waitForController(controllerElement, container);
            } else {
                console.error('❌ Modal element does not have showModal() method');
            }
        }, 150);
    }

    /**
     * Create the media picker modal structure
     * This replicates the _picker_modal.html.erb structure
     */
    createMediaPickerModal(container) {
        const modalHTML = `
            <dialog data-media-picker-target="modal"
                    class="modal w-full max-w-6xl rounded-xl shadow-2xl p-0"
                    style="z-index: 9999; position: fixed; inset: 0; margin: auto; max-height: 90vh; border: none; width: 100%; max-width: 72rem;"
                    >
                <div class="bg-white rounded-xl overflow-hidden">
                    <!-- Modal Header -->
                    <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                        <div>
                            <h2 class="text-xl font-semibold text-gray-900">${this.selectedImageElement ? 'Replace Image' : 'Select Media'}</h2>
                            <p class="text-sm text-gray-600">${this.selectedImageElement ? 'Choose a new image to replace the current one' : 'Choose from your media library'}</p>
                        </div>
                        <button type="button"
                                data-action="click->media-picker#close"
                                class="text-gray-400 hover:text-gray-600">
                            <i class="fas fa-times text-xl"></i>
                        </button>
                    </div>

                    <!-- Breadcrumb Navigation -->
                    <div class="px-6 py-3 bg-gray-50 border-b border-gray-200"
                         data-media-picker-target="breadcrumb">
                        <nav class="flex items-center gap-2 text-sm" aria-label="Breadcrumb">
                            <button type="button"
                                    data-action="click->media-picker#navigateToRoot"
                                    class="flex items-center gap-1 text-gray-600 hover:text-green-600 transition-colors">
                                <i class="fas fa-home"></i>
                                <span>All Media</span>
                            </button>
                        </nav>
                    </div>

                    <!-- Upload Section -->
                    <div class="px-6 py-3 bg-blue-50 border-b border-blue-200">
                        <div class="flex items-center gap-3">
                            <input type="file"
                                   multiple
                                   data-media-picker-target="uploadInput"
                                   data-action="change->media-picker#handleFileUpload"
                                   class="hidden"
                                   accept="image/*,video/*,audio/*,.pdf,.doc,.docx">
                            <button type="button"
                                    data-action="click->media-picker#openFileUpload"
                                    class="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700">
                                <i class="fas fa-upload"></i>
                                Upload Files
                            </button>
                            <span class="text-xs text-gray-600">Upload files to the current folder</span>
                        </div>

                        <!-- Upload Progress -->
                        <div data-media-picker-target="uploadProgress" class="hidden mt-3">
                            <div class="bg-white rounded-lg border border-gray-200 p-3">
                                <div class="flex items-center justify-between mb-2">
                                    <span class="text-sm font-medium text-gray-700">Uploading files...</span>
                                    <span class="text-xs text-gray-500" data-media-picker-target="uploadStatus">0 / 0</span>
                                </div>
                                <div class="w-full bg-gray-200 rounded-full h-2">
                                    <div data-media-picker-target="uploadBar" class="bg-blue-600 h-2 rounded-full transition-all" style="width: 0%"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Search and Filters -->
                    <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
                        <div class="flex gap-3">
                            <div class="flex-1">
                                <input type="text"
                                       placeholder="Search media..."
                                       data-media-picker-target="search"
                                       data-action="input->media-picker#search"
                                       class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                            </div>
                            <div class="flex-shrink-0">
                                <select data-media-picker-target="typeFilter"
                                        data-action="change->media-picker#filterByType"
                                        class="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
                                    <option value="">All Types</option>
                                    <option value="image">Images</option>
                                    <option value="video">Videos</option>
                                    <option value="document">Documents</option>
                                    <option value="audio">Audio</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- Media Grid -->
                    <div class="px-6 py-6 overflow-y-auto relative" style="max-height: 500px; min-height: 300px;">
                        <!-- Loading State - Overlay -->
                        <div data-media-picker-target="loading" class="hidden absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10">
                            <div class="text-center">
                                <i class="fas fa-spinner fa-spin text-4xl text-gray-400 mb-4"></i>
                                <p class="text-gray-600">Loading media...</p>
                            </div>
                        </div>

                        <!-- Empty State -->
                        <div data-media-picker-target="emptyState" class="hidden text-center py-12">
                            <i class="fas fa-image text-6xl text-gray-300 mb-4"></i>
                            <h3 class="text-lg font-medium text-gray-900 mb-2">No media found</h3>
                            <p class="text-gray-500 mb-6">Try adjusting your search or filters, or upload new files using the button above</p>
                        </div>

                        <!-- Grid -->
                        <div data-media-picker-target="grid" class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            <!-- Media items will be inserted here by JavaScript -->
                        </div>

                        <!-- Load More -->
                        <div class="mt-6 text-center">
                            <button type="button"
                                    data-action="click->media-picker#loadMore"
                                    class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                                <i class="fas fa-arrow-down"></i>
                                Load More
                            </button>
                        </div>
                    </div>

                    <!-- Modal Footer -->
                    <div class="px-6 py-4 bg-gray-50 border-t border-gray-200">
                        <div class="flex items-center justify-between">
                            <div class="text-sm text-gray-600">
                                <span data-media-picker-target="selectedCount">0</span> selected
                            </div>
                            <div class="flex gap-2">
                                <button type="button"
                                        data-action="click->media-picker#close"
                                        class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                                    Cancel
                                </button>
                                <button type="button"
                                        data-action="click->media-picker#confirmSelection"
                                        class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">
                                    Select
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </dialog>
        `;

        container.insertAdjacentHTML('beforeend', modalHTML);
        return container.querySelector('dialog');
    }

    /**
     * Wait for Stimulus controller to be ready (with polling)
     */
    waitForController(controllerElement, container, attempts = 0, maxAttempts = 20) {
        const attemptDelay = 50; // Try every 50ms

        if (attempts >= maxAttempts) {
            console.error('❌ Could not find controller after', maxAttempts, 'attempts');
            // Fallback: manually call the API
            this.manuallyLoadMedia();
            return;
        }

        // Try multiple methods to access the controller
        let controller = null;

        // Method 1: Direct property access (some Stimulus versions)
        if (controllerElement.mediaPickerController) {
            controller = controllerElement.mediaPickerController;
            console.log('✅ Found controller via direct property (attempt', attempts + 1, ')');
        }

        // Method 2: Via application.getControllerForElementAndIdentifier
        if (!controller && window.Stimulus) {
            try {
                controller = window.Stimulus.getControllerForElementAndIdentifier(
                    controllerElement,
                    'media-picker'
                );
                if (controller) {
                    console.log('✅ Found controller via Stimulus API (attempt', attempts + 1, ')');
                }
            } catch (e) {
                // Ignore, try next method
            }
        }

        // Method 3: Check the element's controllers property
        if (!controller && controllerElement.controllers) {
            const controllers = Array.from(controllerElement.controllers);
            controller = controllers.find(c => c.identifier === 'media-picker');
            if (controller) {
                console.log('✅ Found controller via controllers array (attempt', attempts + 1, ')');
            }
        }

        if (controller) {
            // Found it! Set up overrides and load media
            this.setupControllerOverrides(controller, container);

            // Trigger loadMedia() to populate the modal
            if (controller.loadMedia) {
                console.log('📥 Loading media...');
                controller.loadMedia();

                // Wait for media to load, then highlight current image and hide loading
                setTimeout(() => {
                    this.hideLoading();
                    if (this.selectedImageElement) {
                        this.highlightCurrentImage();
                    }
                }, 500);
            }
        } else {
            // Not found yet, try again
            setTimeout(() => {
                this.waitForController(controllerElement, container, attempts + 1, maxAttempts);
            }, attemptDelay);
        }
    }

    /**
     * Fallback: manually load media if controller can't be accessed
     */
    async manuallyLoadMedia(folder = null) {
        console.log('📥 Fallback: Manually loading media...', folder ? `folder: ${folder}` : 'root');

        this.currentManualFolder = folder;
        this.usingManualMode = true;

        // Sync folder to Stimulus controller if it connected late
        this.syncFolderToController();

        try {
            const params = new URLSearchParams({ page: 1, per_page: 24, media_type: 'image' });
            if (folder) {
                params.append('folder', folder);
            }

            const response = await fetch(`/admin/media/browse?${params}`);
            const data = await response.json();

            console.log('✅ Media loaded manually:', data);

            const grid = document.querySelector('[data-media-picker-target="grid"]');
            if (grid) {
                grid.innerHTML = '';

                // Render folders first
                if (data.folders && data.folders.length > 0) {
                    data.folders.forEach(folder => {
                        grid.appendChild(this.createFolderCard(folder));
                    });
                }

                // Then render media items
                if (data.media && data.media.length > 0) {
                    data.media.forEach(media => {
                        grid.appendChild(this.createMediaCard(media));
                    });
                }

                // Update breadcrumb
                this.updateManualBreadcrumb(data.current_folder);

                // Show/hide empty state
                const emptyState = document.querySelector('[data-media-picker-target="emptyState"]');
                if ((data.folders?.length || 0) === 0 && (data.media?.length || 0) === 0) {
                    emptyState?.classList.remove('hidden');
                } else {
                    emptyState?.classList.add('hidden');
                }

                this.hideLoading();

                // Take over upload handling in manual mode
                this.setupManualUpload();

                if (this.selectedImageElement) {
                    this.highlightCurrentImage();
                }
            }
        } catch (error) {
            console.error('❌ Failed to load media manually:', error);
            this.hideLoading();
        }
    }

    /**
     * Sync the plugin's current folder to the Stimulus controller (if it connected late)
     */
    syncFolderToController() {
        const container = document.querySelector('[data-controller="media-picker"]');
        if (!container) return;

        let controller = null;
        if (window.Stimulus) {
            try {
                controller = window.Stimulus.getControllerForElementAndIdentifier(container, 'media-picker');
            } catch (e) { /* ignore */ }
        }

        if (controller) {
            controller.currentFolder = this.currentManualFolder;
        }
    }

    /**
     * Take over upload handling so it respects the current folder in manual mode
     */
    setupManualUpload() {
        const uploadBtn = document.querySelector('[data-action="click->media-picker#openFileUpload"]');
        const uploadInput = document.querySelector('[data-media-picker-target="uploadInput"]');

        if (!uploadBtn || !uploadInput) return;

        // Only set up once
        if (this._manualUploadBound) return;
        this._manualUploadBound = true;

        // Clone and replace the button to remove Stimulus action binding
        const newBtn = uploadBtn.cloneNode(true);
        newBtn.removeAttribute('data-action');
        uploadBtn.parentNode.replaceChild(newBtn, uploadBtn);

        // Clone and replace the input to remove Stimulus action binding
        const newInput = uploadInput.cloneNode(true);
        newInput.removeAttribute('data-action');
        uploadInput.parentNode.replaceChild(newInput, uploadInput);

        newBtn.addEventListener('click', () => {
            newInput.click();
        });

        newInput.addEventListener('change', async (event) => {
            const files = event.target.files;
            if (!files || files.length === 0) return;

            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';

            // Show upload progress
            const progressEl = document.querySelector('[data-media-picker-target="uploadProgress"]');
            const statusEl = document.querySelector('[data-media-picker-target="uploadStatus"]');
            const barEl = document.querySelector('[data-media-picker-target="uploadBar"]');

            if (progressEl) {
                progressEl.classList.remove('hidden');
                clearUploadFailures(progressEl);
            }

            let uploaded = 0;
            const total = files.length;
            const failures = [];

            for (const file of files) {
                try {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('title', file.name);

                    if (this.currentManualFolder) {
                        formData.append('folder', this.currentManualFolder);
                    }

                    const response = await fetch('/admin/media/uploads', {
                        method: 'POST',
                        headers: { 'X-CSRF-Token': csrfToken },
                        body: formData
                    });

                    // fetch only rejects on a network error, so a 422 has to be
                    // checked for explicitly - otherwise a rejected upload counts
                    // as a success and the author is told nothing.
                    if (!response.ok) {
                        throw new Error(await uploadErrorMessage(response));
                    }

                    uploaded++;
                    const progress = (uploaded / total) * 100;
                    if (barEl) barEl.style.width = `${progress}%`;
                    if (statusEl) statusEl.textContent = `${uploaded} / ${total}`;
                } catch (error) {
                    console.error('Upload failed for file:', file.name, error);
                    failures.push({ name: file.name, message: error.message });
                }
            }

            renderUploadFailures(progressEl, failures);

            // Reset and reload at current folder
            event.target.value = '';
            setTimeout(() => {
                // Keep the panel up when something failed - it is the only place
                // the author is told why the file is missing from the grid.
                if (progressEl && failures.length === 0) progressEl.classList.add('hidden');
                if (barEl) barEl.style.width = '0%';
                this.showLoading();
                this.manuallyLoadMedia(this.currentManualFolder);
            }, 1000);
        });
    }

    /**
     * Create a folder card for manual mode
     */
    createFolderCard(folder) {
        const card = document.createElement('div');
        card.className = 'relative group cursor-pointer';

        card.innerHTML = `
            <div class="aspect-square bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-colors flex items-center justify-center">
                <i class="fas fa-folder text-6xl text-blue-500"></i>
            </div>
            <div class="mt-2">
                <div class="text-sm font-medium text-gray-900 truncate" title="${escapeHtml(folder.name)}">
                    <i class="fas fa-folder text-xs text-blue-500 mr-1"></i>
                    ${escapeHtml(folder.name)}
                </div>
                <div class="text-xs text-gray-500">
                    ${escapeHtml(folder.count)} item${folder.count !== 1 ? 's' : ''}
                </div>
            </div>
        `;

        card.addEventListener('click', () => {
            this.showLoading();
            this.manuallyLoadMedia(folder.path);
        });

        return card;
    }

    /**
     * Update breadcrumb navigation in manual mode
     */
    updateManualBreadcrumb(currentFolder) {
        const breadcrumbContainer = document.querySelector('[data-media-picker-target="breadcrumb"]');
        if (!breadcrumbContainer) return;

        const nav = breadcrumbContainer.querySelector('nav');
        if (!nav) return;

        let html = `
            <button type="button" class="flex items-center gap-1 text-gray-600 hover:text-green-600 transition-colors manual-breadcrumb-root">
                <i class="fas fa-home"></i>
                <span>All Media</span>
            </button>
        `;

        if (currentFolder) {
            const parts = currentFolder.split('/');
            parts.forEach((part, index) => {
                const path = parts.slice(0, index + 1).join('/');
                const isLast = index === parts.length - 1;

                html += `<i class="fas fa-chevron-right text-xs text-gray-400"></i>`;

                if (isLast) {
                    html += `<span class="font-medium text-gray-900">${escapeHtml(part)}</span>`;
                } else {
                    html += `<button type="button" class="text-gray-600 hover:text-green-600 transition-colors manual-breadcrumb-folder" data-folder-path="${escapeHtml(path)}">${escapeHtml(part)}</button>`;
                }
            });
        }

        nav.innerHTML = html;

        // Bind click handlers
        nav.querySelector('.manual-breadcrumb-root')?.addEventListener('click', () => {
            this.showLoading();
            this.manuallyLoadMedia(null);
        });

        nav.querySelectorAll('.manual-breadcrumb-folder').forEach(btn => {
            btn.addEventListener('click', () => {
                this.showLoading();
                this.manuallyLoadMedia(btn.dataset.folderPath);
            });
        });
    }

    /**
     * Create a media card element (simplified version)
     */
    createMediaCard(media) {
        const card = document.createElement('div');
        card.className = 'relative group cursor-pointer';
        card.dataset.mediaId = media.id;
        card.dataset.blobSignedId = media.blob_signed_id;
        // Stable, full-resolution URL embedded into content on select (never the thumbnail).
        const publicUrl = media.public_url || media.url || '';
        card.dataset.publicUrl = isSafeUrl(publicUrl) ? publicUrl : '';

        const thumbnailHTML = media.media_type === 'image' && isSafeUrl(media.thumbnail_url)
            ? `<img src="${escapeHtml(media.thumbnail_url)}" alt="${escapeHtml(media.title)}" class="w-full h-full object-cover" loading="lazy">`
            : `<div class="flex items-center justify-center h-full bg-gray-200"><i class="fas fa-file text-3xl text-gray-400"></i></div>`;

        card.innerHTML = `
            <div class="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-transparent hover:border-green-500 transition-colors">
                ${thumbnailHTML}
            </div>
            <div class="mt-2">
                <div class="text-sm font-medium text-gray-900 truncate" title="${escapeHtml(media.title)}">
                    ${escapeHtml(media.title)}
                </div>
                <div class="text-xs text-gray-500">
                    ${escapeHtml(media.file_size_human || '')}
                </div>
            </div>
        `;

        // Add click handler to select and insert
        card.addEventListener('click', () => {
            if (card.dataset.publicUrl) {
                this.insertImage(card.dataset.publicUrl, media.title, media.id);
                // Close the modal
                const modal = document.querySelector('[data-media-picker-target="modal"]');
                if (modal) {
                    modal.close();
                }
                // Cleanup container
                const container = modal.closest('[data-controller="media-picker"]');
                if (container) {
                    this.cleanup(container);
                }
            }
        });

        return card;
    }

    /**
     * Highlight the currently selected image in the media grid
     */
    highlightCurrentImage() {
        if (!this.selectedImageElement) return;

        const currentSrc = this.selectedImageElement.src;
        const currentMediaId = this.selectedImageElement.getAttribute('data-media-id');

        console.log('🔍 Looking for current image in grid:', { currentSrc, currentMediaId });

        // Find all media cards
        const cards = document.querySelectorAll('[data-media-picker-target="grid"] [data-media-id]');

        cards.forEach(card => {
            const img = card.querySelector('img');
            const mediaId = card.dataset.mediaId;

            // Check if this card matches the current image (by media ID or src)
            const matchesId = currentMediaId && mediaId === currentMediaId;
            const matchesSrc = img && currentSrc.includes(img.src.split('?')[0]); // Ignore query params

            if (matchesId || matchesSrc) {
                console.log('✅ Found matching image in grid');

                // Add highlight/border to show this is the current image
                const border = card.querySelector('.aspect-square');
                if (border) {
                    border.classList.remove('border-transparent');
                    border.classList.add('border-blue-500', 'ring-2', 'ring-blue-300');
                }

                // Add a badge to indicate this is the current image
                const badge = document.createElement('div');
                badge.className = 'absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full';
                badge.innerHTML = '<i class="fas fa-check mr-1"></i>Current';
                card.appendChild(badge);

                // Scroll into view
                card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        });
    }

    /**
     * Setup controller method overrides for TinyMCE integration
     */
    setupControllerOverrides(controller, container) {
        this.stimulusController = controller;

        // Override the confirmSelection method to insert into TinyMCE
        const originalConfirm = controller.confirmSelection.bind(controller);
        controller.confirmSelection = () => {
            console.log('✅ Confirm selection called');
            const selectedCard = controller.gridTarget.querySelector('[data-media-id].border-green-500');

            if (selectedCard) {
                const title = selectedCard.querySelector('.text-sm').textContent.trim();
                // Prefer the stable full-resolution public URL stored on the card; fall
                // back to the thumbnail only if it is somehow missing.
                const publicUrl = selectedCard.dataset.publicUrl || selectedCard.querySelector('img')?.src;

                if (publicUrl) {
                    const mediaId = selectedCard.dataset.mediaId;

                    // Insert image into TinyMCE
                    this.insertImage(publicUrl, title, mediaId);
                }
            }

            // Close modal and cleanup
            controller.close();
            this.cleanup(container);
        };

        // Override close to cleanup
        const originalClose = controller.close.bind(controller);
        controller.close = () => {
            originalClose();
            this.cleanup(container);
        };
    }

    /**
     * Get the currently selected image in TinyMCE (if any)
     */
    getSelectedImage() {
        const node = this.editor.selection.getNode();

        // Check if the selected node is an image
        if (node && node.nodeName === 'IMG') {
            console.log('✅ Selected node is an image');
            return node;
        }

        // Check if the selection contains an image
        const selectedContent = this.editor.selection.getContent();
        if (selectedContent.includes('<img')) {
            console.log('✅ Selection contains an image');
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = selectedContent;
            const img = tempDiv.querySelector('img');
            if (img) {
                // Find the actual img element in the editor
                const imgs = this.editor.dom.select('img');
                for (const editorImg of imgs) {
                    if (editorImg.src === img.src) {
                        return editorImg;
                    }
                }
            }
        }

        console.log('ℹ️  No image selected');
        return null;
    }

    /**
     * Insert or replace image in TinyMCE
     */
    insertImage(src, alt, mediaId) {
        console.log('🖼️  Inserting/replacing image:', { src, alt, mediaId });

        // src is already the stable, full-resolution public URL (/media/<uuid>).
        const imgSrc = src;

        // Check if we're replacing an existing image
        if (this.selectedImageElement) {
            console.log('🔄 Replacing existing image');

            // Update via editor.dom so TinyMCE's data-mce-src cache follows;
            // it serializes src from data-mce-src, not the live attribute.
            this.editor.undoManager.transact(() => {
                this.editor.dom.setAttribs(this.selectedImageElement, {
                    src: imgSrc,
                    alt: alt,
                    'data-media-id': mediaId
                });
            });

            // Trigger change event
            this.editor.dispatch('change');

            console.log('✅ Image replaced in editor');
        } else {
            console.log('➕ Inserting new image');

            // Insert a new image at cursor position (escape attribute values).
            this.editor.insertContent(
                `<img src="${escapeHtml(imgSrc)}" alt="${escapeHtml(alt)}" data-media-id="${escapeHtml(mediaId)}" />`
            );

            console.log('✅ Image inserted into editor');
        }

        // Clear the selected image reference
        this.selectedImageElement = null;
    }

    /**
     * Show loading spinner
     */
    showLoading() {
        const loading = document.querySelector('[data-media-picker-target="loading"]');
        if (loading) {
            loading.classList.remove('hidden');
            console.log('⏳ Loading spinner shown');
        }
    }

    /**
     * Hide loading spinner
     */
    hideLoading() {
        const loading = document.querySelector('[data-media-picker-target="loading"]');
        if (loading) {
            loading.classList.add('hidden');
            console.log('✅ Loading spinner hidden');
        }
    }

    /**
     * Cleanup the temporary container
     */
    cleanup(container) {
        setTimeout(() => {
            if (container && container.parentNode) {
                container.parentNode.removeChild(container);
            }
        }, 300);
    }
}

export default MediaLibraryPlugin;
